//dependencies
import React, { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ICustomQuestionGetResponse } from "@performyard/models";
import {
	ButtonStandard,
	FormField,
	FormHandle,
	ModalBase,
	PYForm,
	Text,
	TextEditor,
	toast,
} from "@performyard/py-components";

import Button from "../../../../../../../components/Button";
import SelectInput, { Result } from "../../../../../../../components/Inputs/SelectInput";
import { RadioButtons } from "../../../../../../../components/Inputs/SelectionOptions/RadioButtons";
import Tooltip from "../../../../../../../components/Tooltip";

// hooks
import {
	useCreateEngagementSurveyCustomQuestion,
	useMutateEngagementSurveyCustomQuestion,
} from "../../../../../../../api/hooks/defs/engagement-survey-custom-questions";
import { useFeatureFlagQuery } from "../../../../../../../hooks/useFeatureFlag";

// services - import from barrel file so test mocks apply
import { getEngagementCustomFactors } from "../../../../../../../api/services";

// utils
import { stripHtmlTags } from "../../../../../../../utils/stripHtmlTags";
import { getQuestionURL } from "./utils";

const QUESTION_TYPES: Result[] = [
	{
		label: "Rating Scale (Likert-5)",
		value: "likert5",
	},
];

const MAX_QUESTION_LENGTH = 128;

const formSchema = z.object({
	scale: z.enum(["likert5", "text"]),
	question: z
		.string()
		.default("")
		.refine((val) => stripHtmlTags(val).length >= 1, {
			message: "Question text is required",
		})
		.refine((val) => stripHtmlTags(val).length <= MAX_QUESTION_LENGTH, {
			message: `Question must be ${MAX_QUESTION_LENGTH} characters or less`,
		}),
	enabled: z.boolean(),
	factor: z.enum(["Custom"]),
	subfactor: z.string().min(0).max(MAX_QUESTION_LENGTH).nullable(),
	custom_factor: z.string().nullable(),
});

type CustomQuestionFormValues = z.infer<typeof formSchema>;

type CustomQuestionSubmitData = {
	question: string;
	scale: string;
	enabled: boolean;
	factor: string;
	subfactor: string | null;
	custom_factor: string | null;
};

type CustomFactorOption = {
	_id: string;
	name: string;
};

type CreateOrEditCustomSurveyQuestionModalProps = {
	isOpen: boolean;
	onClose?: () => void;
	onSave?: () => void;
	selectedQuestion?: ICustomQuestionGetResponse;
	onSubmit?: (data: CustomQuestionSubmitData) => void;
	// Optional: pass custom factors directly (useful for testing)
	customFactors?: CustomFactorOption[];
};

const CreateOrEditCustomSurveyQuestionModal = ({
	isOpen = false,
	onClose,
	onSave,
	selectedQuestion,
	onSubmit,
	customFactors: customFactorsProp,
}: CreateOrEditCustomSurveyQuestionModalProps) => {
	// Feature flag for custom factors
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	// Track question text length for preview button disabled state
	const [questionTextLength, setQuestionTextLength] = useState(0);

	// Fetch custom factors for the dropdown (or use prop if provided)
	const [customFactorsFromApi, setCustomFactorsFromApi] = useState<CustomFactorOption[]>([]);

	useEffect(() => {
		if (customFactorsProp || !customFactorsEnabled) return;

		const fetchCustomFactors = async () => {
			try {
				const data = await getEngagementCustomFactors();
				setCustomFactorsFromApi(data?.customFactors || []);
			} catch {
				// Silently fail - dropdown will just show "Unassigned"
			}
		};
		fetchCustomFactors();
	}, [customFactorsProp, customFactorsEnabled]);

	const customFactors = customFactorsProp || customFactorsFromApi;

	if (!QUESTION_TYPES.find((type) => type.value === "text")) {
		QUESTION_TYPES.push({
			label: "Comment Box",
			value: "text",
		});
	}

	const handleSave = () => {
		onSave?.();
	};

	const { mutate: createQuestion, isPending: isCreateLoading } = useCreateEngagementSurveyCustomQuestion(
		handleSave,
		(response) => {
			const errors = response?.data?.errors || [];
			toast.error(errors[0].message || "Failed to create custom question");
		}
	);
	const { mutate: editQuestion, isPending: isEditLoading } = useMutateEngagementSurveyCustomQuestion(onSave, () =>
		toast.error("Failed to get Custom Questions")
	);

	useEffect(() => {
		if (isOpen) {
			formRef.current?.reset();
			setQuestionTextLength(stripHtmlTags(selectedQuestion?.question || "").length);
		}
	}, [isOpen, selectedQuestion]);

	const defaultValues: CustomQuestionFormValues = selectedQuestion
		? {
				scale: selectedQuestion.scale as "likert5" | "text",
				question: selectedQuestion.question || "",
				enabled: selectedQuestion.enabled ?? true,
				factor: "Custom" as const,
				subfactor: (selectedQuestion as unknown as { subfactor?: string | null }).subfactor ?? null,
				custom_factor: (selectedQuestion as unknown as { custom_factor?: string | null }).custom_factor ?? null,
			}
		: {
				scale: "likert5",
				question: "",
				enabled: true,
				factor: "Custom",
				subfactor: null,
				custom_factor: null,
			};

	const handleFormSubmit = ({
		value,
	}: {
		value: CustomQuestionFormValues;
		resetForm: () => void;
		setError: (field: keyof CustomQuestionFormValues, error: string) => void;
	}) => {
		const plainTextQuestion = stripHtmlTags(value.question);

		const submitData: CustomQuestionSubmitData = {
			question: plainTextQuestion,
			scale: value.scale,
			enabled: value.enabled,
			factor: value.factor,
			subfactor: value.subfactor,
			custom_factor: value.custom_factor,
		};

		// Call onSubmit if provided (for testing)
		if (onSubmit) {
			onSubmit(submitData);
			return;
		}

		if (selectedQuestion) {
			editQuestion({
				_id: selectedQuestion._id,
				...submitData,
			} as Parameters<typeof editQuestion>[0]);
		} else {
			createQuestion(submitData as Parameters<typeof createQuestion>[0]);
		}
	};

	const handlePreviewClick = () => {
		const formValues = formRef.current?.getFormValues();
		if (!formValues) return;
		const plainText = stripHtmlTags(formValues.question);
		const url = getQuestionURL({ question: plainText, scale: formValues.scale });
		window.open(url, "_blank");
	};

	if (!isOpen) return null;

	return (
		<ModalBase isOpen={isOpen} onClose={onClose ?? (() => {})}>
			<div className="w-[432px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="mb-4 text-center">
					{selectedQuestion ? "Edit Custom Question" : "Create Custom Question"}
				</Text>

				<PYForm
					key={selectedQuestion ? selectedQuestion._id : "create"}
					ref={formRef}
					id="custom-question-form"
					schema={formSchema}
					defaultValues={defaultValues}
					onSubmit={handleFormSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="flex flex-col gap-4">
						{/* Question Type */}
						<FormField<string>
							id="scale"
							input={(field) => (
								<SelectInput
									disabled={selectedQuestion !== undefined}
									label="Question Type"
									labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
									results={QUESTION_TYPES}
									value={{
										value: field.value,
										label: QUESTION_TYPES.find((type) => type.value === field.value)?.label || "",
									}}
									onChange={({ value }) => field.onChange(value)}
								/>
							)}
						/>

						{/* Factor (behind feature flag) */}
						{customFactorsEnabled && (
							<FormField<string | null>
								id="custom_factor"
								input={(field) => (
									<SelectInput
										label="Factor"
										labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
										results={[
											{ label: "Unassigned", value: null },
											...[...customFactors]
												.sort((a, b) => a.name.localeCompare(b.name))
												.map((f) => ({
													label: f.name,
													value: f._id,
												})),
										]}
										value={{
											value: field.value,
											label:
												field.value === null
													? "Unassigned"
													: customFactors.find((f) => f._id === field.value)?.name ||
														"Unassigned",
										}}
										onChange={(result) => field.onChange(result?.value ?? null)}
									/>
								)}
							/>
						)}

						{/* Question Text */}
						<FormField<string>
							id="question"
							label="Question Text"
							validators={{
								onChange: ({ value }) => {
									setQuestionTextLength(stripHtmlTags(value).length);
									return undefined;
								},
							}}
							input={(field) => (
								<TextEditor
									id="question"
									initialValue={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									characterLimit={MAX_QUESTION_LENGTH}
									placeholder="Your question text here"
									minHeight={96}
								/>
							)}
						/>

						{/* Question Preview */}
						<div className="-mt-2">
							<Button
								size="micro"
								onClick={handlePreviewClick}
								icon={faMagnifyingGlass}
								iconPosition="left"
								disabled={questionTextLength < 1 || questionTextLength > MAX_QUESTION_LENGTH}
							>
								Question Preview
							</Button>
						</div>

						{/* Question Status */}
						<FormField<boolean>
							id="enabled"
							input={(field) => (
								<div>
									<div className="mb-1 flex items-center gap-0.5">
										<Text size="paragraph-3" weight="semi-bold">
											Question Status
										</Text>
										<span className="self-start pt-0.5 text-[9px] text-primary-9 opacity-70 hover:opacity-100">
											<Tooltip
												message="Control if a question is available for use in upcoming surveys."
												placement="right"
											>
												<FontAwesomeIcon icon={faCircleInfo} />
											</Tooltip>
										</span>
									</div>
									<RadioButtons
										options={[
											{ label: "Enabled", value: "enabled" },
											{ label: "Disabled", value: "disabled" },
										]}
										value={{
											value: field.value ? "enabled" : "disabled",
											label: field.value ? "Enabled" : "Disabled",
										}}
										onChange={({ value }) => field.onChange(value === "enabled")}
									/>
								</div>
							)}
						/>

						{/* Action Buttons */}
						<div className="flex justify-center gap-2">
							<div>
								<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
									Cancel
								</ButtonStandard>
							</div>
							<div>
								<ButtonStandard
									variant="solid"
									color="primary"
									size="large"
									type="button"
									disabled={isCreateLoading || isEditLoading}
									onClick={async () => {
										const isValid = await formRef.current?.validate(true);
										if (isValid) {
											await formRef.current?.submit();
										}
									}}
								>
									{selectedQuestion ? "Save Question" : "Create Question"}
								</ButtonStandard>
							</div>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default CreateOrEditCustomSurveyQuestionModal;
